import { DiGraph } from 'digraph-js';

import type { FieldEditorValues, SectionEditorValues } from './config';

/**
 * Builds a directed graph of conditional logic dependencies across all fields
 * and sections. An edge from A → B means "B's visibility depends on A".
 *
 * Used to detect cycles before saving conditional logic rules.
 */
export const buildConditionalLogicGraph = ({
  fieldConfigs,
  sectionConfigs,
  containers,
}: {
  fieldConfigs: Record<string, FieldEditorValues>;
  sectionConfigs: Record<string, SectionEditorValues>;
  containers: Record<string, string[]>;
}) => {
  const graph = new DiGraph();

  // Add all field IDs as vertices
  for (const fieldId of Object.keys(fieldConfigs)) {
    graph.addVertices({ id: `field:${fieldId}`, adjacentTo: [], body: {} });
  }

  // Add all section IDs as vertices
  for (const sectionId of Object.keys(sectionConfigs)) {
    graph.addVertices({
      id: `section:${sectionId}`,
      adjacentTo: [],
      body: {},
    });
  }

  // Ensure vertices exist for any referenced ifField IDs that may not have
  // their own config entry yet
  const ensureVertex = (id: string) => {
    try {
      graph.addVertices({ id, adjacentTo: [], body: {} });
    } catch {
      // Vertex already exists — ignore
    }
  };

  // Add edges for field conditional logic rules
  for (const [fieldId, config] of Object.entries(fieldConfigs)) {
    if (
      !config.conditionalLogicEnabled ||
      !config.conditionalLogicRules?.length
    ) {
      continue;
    }
    for (const rule of config.conditionalLogicRules) {
      if (rule.ifField) {
        ensureVertex(`field:${rule.ifField}`);
        graph.addEdge({
          from: `field:${rule.ifField}`,
          to: `field:${fieldId}`,
        });
      }
    }
  }

  // Add edges for section conditional logic rules
  for (const [sectionId, config] of Object.entries(sectionConfigs)) {
    if (
      !config.conditionalLogicEnabled ||
      !config.conditionalLogicRules?.length
    ) {
      continue;
    }
    for (const rule of config.conditionalLogicRules) {
      if (rule.ifField) {
        ensureVertex(`field:${rule.ifField}`);
        graph.addEdge({
          from: `field:${rule.ifField}`,
          to: `section:${sectionId}`,
        });
      }
    }
  }

  // Add implicit edges: if a field belongs to a section with conditional logic,
  // then the section's visibility affects the field's visibility
  for (const [sectionId, fieldIds] of Object.entries(containers)) {
    if (
      sectionConfigs[sectionId]?.conditionalLogicEnabled &&
      sectionConfigs[sectionId]?.conditionalLogicRules?.length
    ) {
      for (const fieldId of fieldIds) {
        ensureVertex(`field:${fieldId}`);
        ensureVertex(`section:${sectionId}`);
        try {
          graph.addEdge({
            from: `section:${sectionId}`,
            to: `field:${fieldId}`,
          });
        } catch {
          // Edge may already exist — ignore
        }
      }
    }
  }

  return graph;
};

export interface CycleCheckContext {
  entityType: 'field' | 'section';
  entityId: string;
  fieldConfigs: Record<string, FieldEditorValues>;
  sectionConfigs: Record<string, SectionEditorValues>;
  containers: Record<string, string[]>;
  /** Vertex id (`field:<id>` / `section:<id>`) → label for error messages. */
  getEntityLabel?: (vertexId: string) => string;
}

const buildPatchedGraph = ({
  entityType,
  entityId,
  proposedRules,
  fieldConfigs,
  sectionConfigs,
  containers,
}: Omit<CycleCheckContext, 'getEntityLabel'> & {
  proposedRules: { ifField: string }[];
}) => {
  const patchedFieldConfigs = { ...fieldConfigs };
  const patchedSectionConfigs = { ...sectionConfigs };

  if (entityType === 'field') {
    patchedFieldConfigs[entityId] = {
      ...patchedFieldConfigs[entityId],
      conditionalLogicEnabled: proposedRules.length > 0,
      conditionalLogicRules: proposedRules.map((r) => ({
        ifField: r.ifField,
        values: [],
      })),
    };
  } else {
    patchedSectionConfigs[entityId] = {
      ...patchedSectionConfigs[entityId],
      conditionalLogicEnabled: proposedRules.length > 0,
      conditionalLogicRules: proposedRules.map((r) => ({
        ifField: r.ifField,
        values: [],
      })),
    };
  }

  return buildConditionalLogicGraph({
    fieldConfigs: patchedFieldConfigs,
    sectionConfigs: patchedSectionConfigs,
    containers,
  });
};

/** True if the proposed rules would introduce a cycle. */
export const wouldCreateCycle = ({
  entityType,
  entityId,
  proposedRules,
  fieldConfigs,
  sectionConfigs,
  containers,
}: CycleCheckContext & {
  proposedRules: { ifField: string }[];
}): boolean =>
  buildPatchedGraph({
    entityType,
    entityId,
    proposedRules,
    fieldConfigs,
    sectionConfigs,
    containers,
  }).hasCycles();

/**
 * Vertex ids of the first cycle (in path order), or `null` when no cycle
 * exists.
 */
export const findCycleVertices = ({
  entityType,
  entityId,
  proposedRules,
  fieldConfigs,
  sectionConfigs,
  containers,
}: CycleCheckContext & {
  proposedRules: { ifField: string }[];
}): string[] | null => {
  const graph = buildPatchedGraph({
    entityType,
    entityId,
    proposedRules,
    fieldConfigs,
    sectionConfigs,
    containers,
  });
  const cycles = graph.findCycles();

  return cycles.length > 0 ? cycles[0].map(String) : null;
};

/**
 * Drops rules whose `ifField` is in `deletedFieldIds`. Disables the toggle
 * when a config's rules end up empty. Untouched configs are returned by
 * reference.
 */
export const pruneRuleReferencesToFields = <
  T extends {
    conditionalLogicEnabled: boolean;
    conditionalLogicRules?: { ifField: string; values: string[] }[];
  },
>(
  configs: Record<string, T>,
  deletedFieldIds: string[]
): Record<string, T> => {
  if (deletedFieldIds.length === 0) {
    return configs;
  }
  const deleted = new Set(deletedFieldIds);
  const next: Record<string, T> = { ...configs };
  for (const [id, config] of Object.entries(configs)) {
    if (!config.conditionalLogicRules?.length) {
      continue;
    }
    const filtered = config.conditionalLogicRules.filter(
      (rule) => !deleted.has(rule.ifField)
    );
    if (filtered.length !== config.conditionalLogicRules.length) {
      next[id] = {
        ...config,
        conditionalLogicRules: filtered,
        conditionalLogicEnabled:
          filtered.length > 0 && config.conditionalLogicEnabled,
      };
    }
  }

  return next;
};
